import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useSubscription } from "@/hooks/useSubscription";
import { ClubSubscribeModal as SubscribeModal } from "@/components/club/ClubCheckout";

interface SN_MembershipCTAProps {
  className?: string;
  showIcon?: boolean;
  /** Optional override label for the default (start membership) state */
  startLabel?: string;
}

/**
 * Auth + subscription aware CTA for the homepage membership section.
 *
 * - Logged out: navigates to /login (sign-in; the Club takes no new sign-ups) with a redirect back to /?subscribe=1
 * - Logged in, no active sub: opens the SubscribeModal inline
 * - Logged in, active sub: links to /ai-investing-club with "Go to Club"
 */
export const SN_MembershipCTA = ({
  className = "",
  showIcon = true,
  startLabel = "Start Membership",
}: SN_MembershipCTAProps) => {
  const { user, isAdmin, isLoading: authLoading } = useAuth();
  const { isSubscribed, isLoading: subLoading } = useSubscription(user);
  const [showSubscribe, setShowSubscribe] = useState(false);
  const navigate = useNavigate();

  const loading = authLoading || (!!user && subLoading);

  // Already a paying member (or admin) -> send to the hub instead of showing "Start Membership"
  if (user && (isSubscribed || isAdmin)) {
    return (
      <Link to="/ai-investing-club" className={className}>
        {showIcon && <Sparkles className="w-4 h-4 mr-2" />}
        Go to Club
        <ArrowRight className="ml-1.5 w-4 h-4" />
      </Link>
    );
  }

  const handleClick = () => {
    if (loading) return;
    if (!user) {
      const redirect = encodeURIComponent("/?subscribe=1");
      navigate(`/login?redirect=${redirect}`);
      return;
    }
    setShowSubscribe(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        className={className}
      >
        {loading ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          showIcon && <Sparkles className="w-4 h-4 mr-2" />
        )}
        {startLabel}
      </button>
      <SubscribeModal isOpen={showSubscribe} onClose={() => setShowSubscribe(false)} />
    </>
  );
};

export default SN_MembershipCTA;